import { trpc } from '@web/lib/trpc';

import { Button } from '@web/components/ui/button';
import { ContactAvatar } from './contact-avatar';
import { FilePlus, Info, ChevronLeft, Menu } from 'lucide-react';

import { Route } from '@web/routes/dashboard/route';

interface ChatHeaderProps {
  activeContactId: string;
  isMobileView?: boolean;
  onBackToList?: () => void;
}

export function ChatHeader({
  activeContactId,
  isMobileView = false,
  onBackToList,
}: ChatHeaderProps) {
  const { setActiveContractId, navbarOpen, setNavbarOpen } =
    Route.useRouteContext();

  const { contactInfoSidebarContactId, setContactInfoSidebarContactId } =
    Route.useRouteContext();

  const { data: activeContact } = trpc.messaging.getContact.useQuery({
    contactId: activeContactId,
  });

  const { mutateAsync: createNewContractAsync } =
    trpc.contracts.createContractWithDefaults.useMutation();

  const handleCreateNewContract = async () => {
    if (!activeContactId) return;

    const newContract = await createNewContractAsync({
      contactId: activeContactId,
    });

    if (!newContract) return;

    setActiveContractId(newContract.contractId);
  };

  const handleOpenContactInfo = () => {
    setContactInfoSidebarContactId(
      contactInfoSidebarContactId === activeContactId ? null : activeContactId,
    );
  };

  return (
    activeContact && (
      <div className="flex h-16 items-center justify-between border-b bg-white px-3 shadow-sm sm:px-5">
        <>
          <div className="flex grow items-center">
            {/* Back button for mobile view */}
            {isMobileView && onBackToList && (
              <Button
                variant="ghost"
                size="icon"
                className="mr-2 size-8 rounded-full p-0 hover:bg-gray-100 sm:hidden"
                onClick={(e) => {
                  e.preventDefault();
                  // Call the back function to clear the URL and return to contacts list
                  onBackToList();
                }}
                aria-label="Back to contacts"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}

            {/* Menu button on mobile only */}
            {isMobileView && (
              <Button
                variant="ghost"
                size="icon"
                className="mr-2 size-8 rounded-full p-0 hover:bg-gray-100 sm:hidden"
                onClick={() => {
                  setNavbarOpen(!navbarOpen);
                }}
                aria-label="Menu"
              >
                <Menu className="h-4 w-4" />
              </Button>
            )}

            <div className="relative">
              <ContactAvatar
                name={`${activeContact.firstName} ${activeContact.lastName}`}
              />
              <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border border-white bg-green-500"></span>
            </div>

            <div className="ml-3 grow overflow-hidden">
              <div className="flex flex-col">
                <p className="truncate text-sm font-medium">
                  {`${activeContact.firstName} ${activeContact.lastName}`}
                </p>
                <p className="text-xs text-gray-500">Online</p>
              </div>
            </div>
          </div>

          <div className="flex space-x-1">
            {/* Contract actions */}
            <Button
              variant="ghost"
              size="icon"
              className="flex size-8 rounded-full p-0 bg-blue-50 text-blue-600 hover:bg-blue-100"
              aria-label="New Contract"
              onClick={() => {
                void handleCreateNewContract();
              }}
            >
              <FilePlus className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="size-8 rounded-full p-0 hover:bg-gray-100"
              aria-label="Contact Info"
              onClick={handleOpenContactInfo}
            >
              <Info className="h-4 w-4" />
            </Button>
          </div>
        </>
      </div>
    )
  );
}
